import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import { Contract, useArchiveBasketMutation, useLazyBasketConnectedContractsQuery } from '../../../api'
import * as dt from '../../../ui/design-tokens/tokens'

type ArchivableBasket = { id: string; name: string }

/**
 * What the connected-contracts lookup produced. `null` means the lookup FAILED — distinct from an
 * empty array, and never collapsed into one: a basket whose contracts we could not read is not a
 * basket with no contracts.
 */
type ConnectedContracts = Contract[] | null

const contractCount = (n: number) => `${n} ${n === 1 ? 'contract' : 'contracts'}`

/** What archiving destroys, in both dialogs — the part that un-archiving cannot undo. */
const DESTRUCTIVE_EFFECT =
  'deletes any tier or rebate rule that only measures this basket. Un-archiving does not put it back.'

/** Links open in a new tab so the confirmation stays up while the user checks several contracts. */
const ContractLinks: React.FC<{ contracts: Contract[] }> = ({ contracts }) => (
  <ContractList>
    {contracts.map(contract => (
      <li key={contract.id}>
        <a href={`/analytics/contracts/${contract.id}/update?step=1`} target="_blank" rel="noreferrer">
          {contract.name}
        </a>
        {contract.code ? <Muted> · {contract.code}</Muted> : null}
      </li>
    ))}
  </ContractList>
)

/**
 * Archiving a basket disconnects it from every contract using it, and drops the tier / rebate rules
 * scoped to it — that is not recoverable by un-archiving, so the user gets the affected contracts
 * (with links to edit them first) before the mutation runs.
 *
 * The connected list is fetched twice: once to build the warning, then again when the user confirms.
 * The dialog can sit open for a while (that is the point — the user is meant to go fix contracts in
 * another tab), so the second read is what the decision is actually made on. If it comes back empty
 * the archive is clean and runs straight through; if contracts are still attached the user has to
 * accept that explicitly via Force Archive.
 *
 * A lookup that FAILS is its own case, never folded into "connected to nothing". The user is told
 * the check did not run and has to accept an unverified archive — but only once: if they already
 * accepted it on the first dialog, a second failure adds no information and does not re-prompt.
 */
export function useArchiveBasketWithWarning(onArchived?: (basket: ArchivableBasket) => void) {
  const [archiveBasket] = useArchiveBasketMutation()
  const [loadConnectedContracts] = useLazyBasketConnectedContractsQuery()

  const runArchive = useCallback(
    (basket: ArchivableBasket) => archiveBasket({ variables: { id: basket.id } }).then(() => onArchived?.(basket)),
    [archiveBasket, onArchived],
  )

  /** Resolves to `null` on a network or GraphQL error, or on a response that carries no data. */
  const fetchConnectedContracts = useCallback(
    (basketId: string): Promise<ConnectedContracts> =>
      loadConnectedContracts({ variables: { basketId } })
        .then(res => (res.error || !res.data ? null : res.data.basketConnectedContracts))
        .catch(() => null),
    [loadConnectedContracts],
  )

  /** First dialog: what archiving will do, and which contracts to review first. */
  const confirmDisconnect = useCallback(
    (basket: ArchivableBasket, contracts: ConnectedContracts) =>
      new Promise<boolean>(resolve => {
        const unverified = contracts === null
        Modal.confirm({
          title: unverified
            ? `Archive “${basket.name}” without checking its contracts?`
            : contracts.length
            ? `Archive “${basket.name}” and disconnect it from contracts?`
            : 'Archive basket?',
          icon: <ExclamationCircleOutlined />,
          width: unverified || contracts.length ? 560 : undefined,
          okText: unverified ? 'Archive anyway' : 'Archive basket',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          content: unverified ? (
            <div>
              <p>
                We couldn’t load the contracts that use this basket, so there is no way to tell you what archiving it
                would disconnect. Archiving removes the basket from every contract using it and {DESTRUCTIVE_EFFECT}
              </p>
              <p>Cancel and try again to see the list first.</p>
            </div>
          ) : contracts.length ? (
            <div>
              <p>
                This basket is used by {contractCount(contracts.length)}. Archiving it removes the basket from{' '}
                {contracts.length === 1 ? 'that contract' : 'those contracts'} and {DESTRUCTIVE_EFFECT}
              </p>
              <p>Open a contract to review or update its baskets and rules first:</p>
              <ContractLinks contracts={contracts} />
            </div>
          ) : (
            <p>
              {`“${basket.name}” is not used by any contract. You can restore it from the Archived tab at any time.`}
            </p>
          ),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        })
      }),
    [],
  )

  /**
   * Second dialog: at the moment of archiving the basket is either still connected, or its state
   * could not be verified. Either way the user has to opt in again.
   */
  const confirmForceArchive = useCallback(
    (basket: ArchivableBasket, contracts: ConnectedContracts) =>
      new Promise<boolean>(resolve => {
        Modal.confirm({
          title: contracts ? `Still connected to ${contractCount(contracts.length)}` : 'Couldn’t check the contracts',
          icon: <ExclamationCircleOutlined />,
          width: 560,
          okText: 'Force Archive',
          okButtonProps: { danger: true },
          cancelText: 'Keep basket',
          content: contracts ? (
            <div>
              <p>
                “{basket.name}” is still used by {contractCount(contracts.length)}. Forcing the archive disconnects it
                from {contracts.length === 1 ? 'it' : 'them'} now and {DESTRUCTIVE_EFFECT}
              </p>
              <ContractLinks contracts={contracts} />
            </div>
          ) : (
            <p>
              We couldn’t re-check which contracts use “{basket.name}”. Forcing the archive disconnects it from every
              contract using it — however many that turns out to be — and {DESTRUCTIVE_EFFECT}
            </p>
          ),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        })
      }),
    [],
  )

  /**
   * Resolves `true` once the basket is archived, `false` if the user backed out at either dialog,
   * and rejects if the mutation fails — callers own the messaging.
   */
  const confirmAndArchive = useCallback(
    async (basket: ArchivableBasket): Promise<boolean> => {
      const initial = await fetchConnectedContracts(basket.id)
      if (!(await confirmDisconnect(basket, initial))) {
        return false
      }

      // Re-read: the user may have just detached the basket in another tab, or attached it elsewhere.
      const recheck = await fetchConnectedContracts(basket.id)
      // A failed re-check still needs an explicit yes — unless the first dialog was already the
      // unverified one, in which case the user has answered this exact question a moment ago.
      const needsForceArchive = recheck === null ? initial !== null : recheck.length > 0
      if (needsForceArchive && !(await confirmForceArchive(basket, recheck))) {
        return false
      }

      await runArchive(basket)
      return true
    },
    [confirmDisconnect, confirmForceArchive, fetchConnectedContracts, runArchive],
  )

  return { confirmAndArchive }
}

const ContractList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  max-height: 220px;
  overflow-y: auto;

  li {
    margin-bottom: 4px;
  }
`

const Muted = styled.span`
  color: ${dt.ColorNeutral60};
`
